import { motion } from "framer-motion";
import { Camera, Star } from "lucide-react";
import { WhatsAppIcon } from "../components/icons";
import { BRAND, DOT_COLORS, FRAME_COLORS, TRIPS } from "../data/site";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

export default function OurTrips() {
  return (
    <div data-testid="our-trips-page" className="pt-[72px]">
      <section className="noise-dark bg-navy py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">
            Our Happy Clients
          </p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold tracking-tighter text-white sm:text-5xl lg:text-6xl">
            Real trips. Real travellers. Real smiles.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
            Moments from the holidays we have planned — honeymoons, family
            trips, group tours and corporate outings across the world.
          </p>
        </div>
      </section>

      {/* GOOGLE RATING */}
      <section className="mx-auto max-w-7xl px-4 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.5 }}
          data-testid="google-rating-card"
          className="flex flex-col items-center gap-6 rounded-3xl border border-black/5 bg-white p-8 text-center shadow-sm sm:flex-row sm:justify-between sm:p-10 sm:text-left"
        >
          <div className="flex items-center gap-6">
            <div className="font-display text-6xl font-extrabold tracking-tighter text-navy">
              {BRAND.googleRating}
            </div>
            <div>
              <div className="relative flex w-fit">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-6 w-6 text-gray-300" />
                  ))}
                </div>
                <div
                  className="absolute inset-0 flex gap-1 overflow-hidden"
                  style={{ width: "94%" }}
                >
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      data-testid="trips-google-star"
                      className="h-6 w-6 shrink-0 fill-gold text-gold"
                    />
                  ))}
                </div>
              </div>
              <p className="mt-2 text-sm font-bold uppercase tracking-[0.15em] text-gray-500">
                Rated on Google
              </p>
              <a
                href={BRAND.maps}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="trips-google-reviews-link"
                className="mt-1 text-sm font-semibold text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors duration-200 hover:text-gold-deep"
              >
                Read our reviews on Google
              </a>
            </div>
          </div>
          <a
            href={BRAND.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="trips-whatsapp-cta"
            className="flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 font-display font-bold text-white transition-transform duration-200 hover:scale-[1.03]"
          >
            <WhatsAppIcon className="h-5 w-5" />
            Become the next happy client
          </a>
        </motion.div>
      </section>

      {/* GALLERY */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div
          data-testid="trips-gallery"
          className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4"
        >
          {TRIPS.map((t, i) => (
            <motion.figure
              key={t.src}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
              data-testid={`client-photo-${i + 1}`}
              className={`overflow-hidden rounded-2xl border-4 bg-white shadow-md ${FRAME_COLORS[i % FRAME_COLORS.length]}`}
            >
              <img
                src={t.src}
                alt={`${t.place} — Manvi Travels client trip`}
                loading="lazy"
                className="aspect-[4/5] w-full object-cover"
              />
              <figcaption className="flex items-center justify-center gap-2 px-3 py-2.5">
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${DOT_COLORS[i % DOT_COLORS.length]}`}
                />
                <span className="truncate font-display text-xs font-bold text-navy sm:text-sm">
                  {t.place}
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-center gap-4 rounded-3xl border border-dashed border-black/15 bg-white/60 p-10 text-center">
          <Camera className="h-8 w-8 text-teal" />
          <p className="max-w-md text-base text-gray-600">
            More photos from our trips will be added soon — this gallery keeps
            growing with every happy traveller.
          </p>
        </div>
      </section>
    </div>
  );
}
